(ns nosco.ui.menus
  (:require
   ["focus-group" :as create-focus-group]
   [clojure.string]
   [goog.object :as obj]
   [hx.hooks :as hooks]
   [hx.react :refer [defnc]]
   [nosco.colors :as colors]
   [nosco.i18n.translate :as t]
   [nosco.icons2 :as icons]
   [nosco.react :as nosco-react]
   [nosco.ui.buttons2 :as buttons2]
   [nosco.ui.checkbox :as checkbox]
   [nosco.ui.inputs :as inputs]
   [nosco.ui.loading :as loading]
   [nosco.ui.popup :as popup]
   [nosco.ui.text :as text]
   [react :as React]
   [shadow.resource]
   [nosco.controllers :as nc]))

(defonce FocusGroupContext (React/createContext nil))

(defn use-focus-group
  ([ref]
   (use-focus-group ref false))
  ([ref disabled]
   (let [focus-group (hooks/useContext FocusGroupContext)]
     (hooks/useEffect (fn []
                        (when (and focus-group (not disabled))
                          (when-let [menu-item-node (.-current ref)]
                            (.addMember focus-group menu-item-node)
                            ;; tabIndex is not necessary for buttons, but it is for non-focusable elements and for
                            ;; focus-visible to be triggered on links
                            (obj/set menu-item-node "tabIndex" 0))))
                      [focus-group disabled]))))

(defnc SubmitPostMenuItem [{:keys [id img-src title description href on-click]}]
  [:div {:class "nosco-submit-channel-menu-item-wrap"}
   [:a {:href href
        :on-click on-click
        :class ["link-button-reset" "nosco-submit-channel-menu-item"]
        :role "menuitem"}
    [:div {:class "nosco-submit-channel-menu-item-img-wrap"}
     (if-not (clojure.string/blank? img-src)
       [:img {:src img-src
              :class "nosco-submit-channel-menu-item-img"}]
       [icons/PlaceholderImage {:variant :channel
                                :id id
                                :class ["nosco-submit-channel-menu-item-img"
                                        "nosco-submit-channel-menu-item-img--placeholder"]}])]
    [:div {:class "nosco-submit-channel-menu-item-text-wrap"}
     [text/Text {:el :div
                 :variant text/Heading-Sm
                 :class ["nosco-submit-channel-menu-item-title" "text-neutral-900"]}
      title]
     [text/Text {:el :div
                 :variant text/Body-Sm-Regular
                 :class ["nosco-submit-channel-menu-item-desc" "text-neutral-700"]}
      description]]
    [buttons2/ButtonBase {:variant :primary-black
                          :icon icons/Create-small}]]])

(defnc SearchMenu [{:keys [children search-value on-search-change loading?]}]
  [:div {:class "nosco-search-menu"
         :role "menu"}
   [:div {:class "nosco-search-menu__header"}
    [inputs/SearchInput {:placeholder (t/t "Search channels")
                         :value search-value
                         :on-change on-search-change}]]
   [:div {:class "nosco-search-menu__guts"
          :style {:opacity (if loading? 0.5 1.0)}}
    children]])

(defnc MenuItem [{:keys [el label icon dropdown selected disabled size] :as props} ref]
  (let [ref (or ref (React/useRef))
        el (if el el (if (:href props) :a :button))]
    (use-focus-group ref)
    [el
     (cond-> {:class ["nosco-menu-item"
                      (when (= :sm size) "nosco-menu-item--size-sm")
                      (when selected "nosco-menu-item--selected")
                      (when (= :button el) "nosco-button-reset")
                      (when disabled "nosco-menu-item--disabled")]
              :ref ref}
       true (nosco-react/merge-props (dissoc props :el :label :icon :dropdown :disabled))
       (= el :button) (assoc :type "button")
       disabled (dissoc :href))
     (when icon
       [:span {:class ["nosco-menu-item__icon"]}
        (if (React/isValidElement icon)
          icon
          [icons/Icon
           {:icon icon
            :color (if disabled
                     colors/neutral-300
                     colors/neutral-400)}])])
     [:span {:class ["nosco-menu-item__label"]}
      label]
     (when dropdown
       [icons/Icon
        {:class ["ml-auto"]
         :icon icons/ChevronDown-small}])]))

(nosco-react/forward-ref! MenuItem)

(defnc MenuItemDropdown [{:keys [label icon children]}]
  (let [ref (React/useRef)
        [open ->open] (hooks/useState false)
        focus-group (hooks/useMemo (fn [] (create-focus-group))
                                   [open])
        close-and-return-focus! (fn [] (->open false) (.focus (.-current ref)))]
    (hooks/useEffect (fn []
                       (when focus-group
                         (.activate focus-group)
                         (when open
                           (.focusNodeAtIndex focus-group 0)))
                       (fn []
                         (when focus-group
                           (.deactivate focus-group))))
                     [open])
    (popup/use-escape open close-and-return-focus!)
    [:<>
     [MenuItem
      {:el :button
       :dropdown true
       :label label
       :icon icon
       :ref ref
       :on-click
       (fn [^js event]
         (.stopPropagation event)
         (->open not))}]
     (when open
       [FocusGroupContext.Provider {:value focus-group}
        [:div {:class ["nosco-menu-item-dropdown__items"]}
         children]])]))

(defnc MenuItemChannel [{:keys [id img-src label] :as props}]
  (let [ref (React/useRef)]
    (use-focus-group ref)
    [:a
     (-> {:class ["nosco-menu-item" "nosco-menu-item-channel"]
          :ref ref}
         (nosco-react/merge-props (dissoc props :img-src :label)))
     [:span {:class ["nosco-menu-item-channel__img-wrap"]}
      (if img-src
        [:img {:class ["nosco-menu-item-channel__img"] :src img-src}]
        [icons/PlaceholderImage {:variant :channel
                                 :id id
                                 :class ["nosco-menu-item-channel__img"
                                         "nosco-menu-item-channel__img--placeholder"]}])]
     [:span {:class ["nosco-menu-item__label"]}
      label]]))

(defnc MenuItemAdminHeader [{:keys [text]}]
  [:div {:class "nosco-menu-item-admin-header"}
   text])

(defnc MenuItemAdmin [{:keys [el
                              icon
                              label
                              disabled
                              dangerous
                              selected
                              variant]
                       :or {el :button
                            variant :normal}
                       :as props}]
  (let [ref (React/useRef)]
    (use-focus-group ref disabled)
    [el (cond-> {:class ["nosco-menu-item-admin"
                         "nosco-button-reset"
                         (when dangerous "nosco-menu-item-admin--dangerous")
                         (when disabled "nosco-menu-item-admin--disabled")
                         (when selected "nosco-menu-item-admin--selected")]
                 :ref ref}
          disabled (assoc :disabled true)
          (= el :button) (assoc :type "button")

          true
          (nosco.react/merge-props (dissoc props :el :icon :label :disabled :dangerous :selected :variant)))
     (when icon
       [icons/Icon {:icon icon
                     ;; optically align icon vertically center
                    :icon-style {:margin-top 2}
                    :color (if disabled
                             colors/neutral-300
                             colors/neutral-400)}])
     [text/Text {:variant text/Body-Md-Regular
                 :class ["nosco-menu-item-admin__label"
                         (when (= variant :destructive)
                           "text-error-500")]}
      label]
     (when selected
       [:div {:class "nosco-menu-item-admin__selected-icon"}
        [icons/Icon {:color colors/blue-600
                     :icon icons/Done-small}]])]))

(defnc MenuItemCheckbox [{:keys [label disabled selected on-click]}]
  (let [ref (React/useRef)]
    (use-focus-group ref disabled)
    [:div (cond-> {:class ["nosco-menu-item-admin"
                           (when disabled "nosco-menu-item-admin--disabled")
                           (when selected "nosco-menu-item-admin--selected")]
                   :ref ref
                   :on-key-down
                   (fn [^js event]
                     (when (= "Enter" (.-code event))
                       (on-click)))}
            disabled (assoc :disabled true))
     [checkbox/CheckboxWithLabel {:variant :default
                                  :checked (boolean selected)
                                  :disabled disabled
                                  :on-click on-click
                                  :label label}]]))

(defnc MenuItemAdminLoading []
  [:div {:class ["nosco-menu-item-admin--disabled"]}
   [loading/LoadingNew]])

(defnc MenuItemSpace [{:keys [el color label disabled selected on-click] :or {el :button}}]
  (let [ref (React/useRef)]
    (use-focus-group ref disabled)
    [el (cond-> {:class ["nosco-menu-item-admin"
                         "nosco-button-reset"
                         (when disabled "nosco-menu-item-admin--disabled")
                         (when selected "nosco-menu-item-admin--selected")]
                 :on-click on-click
                 :ref ref}
          disabled (assoc :disabled true)
          (= el :button) (assoc :type "button"))
     [:div {:class "nosco-menu-item-admin__space-color"
            :style {:background-color color}}]
     [text/Text {:variant text/Body-Md-Regular
                 :class "nosco-menu-item-admin__label"}
      label]
     (when selected
       [:div {:class "nosco-menu-item-admin__selected-icon"}
        [icons/Icon {:color colors/blue-600
                     :icon icons/Done-small}]])]))

(defnc MenuItems [{:keys [children]}]
  [:div {:class ["nosco-menu__items"]}
   children])

(defnc MenuSection [{:keys [children]}]
  [:div {:class ["nosco-menu__section"]}
   children])

(defnc Menu [{:keys [children class min-width max-height variant keyboard-nav] :or {variant :more-round}}]
  (let [;; When keyboard-nav is true, enable keyboard navigation using focus-group
        focus-group (hooks/useMemo (fn [] (when keyboard-nav (create-focus-group))))
        style (cond-> {}
                (some? min-width) (assoc :min-width min-width)
                max-height (assoc :max-height max-height))]
    (hooks/useEffect
     (fn []
       (when focus-group
         (.activate focus-group)
         (.focusNodeAtIndex focus-group 0))
       (fn []
         (when focus-group
           (.deactivate focus-group))))
     [])
    [FocusGroupContext.Provider {:value focus-group}
     [:div {:class (nosco-react/merge-class
                    ["nosco-menu"
                     (when (= variant :more-round) "nosco-menu--more-round")
                     (when (= variant :less-round) "nosco-menu--less-round")]
                    class)
            :style style}
      children]]))
